// Esquema zod de la ficha de dimensionado. Es la fuente única de verdad
// del formulario: backend y frontends lo importan y validan con él.
//
// - El backend lo usa para validar el POST de creación/edición.
// - El frontend lo usa para validar antes de enviar (mensajes locales).
// - El paquete `shared` exporta también los tipos derivados (`FichaInput`,
//   `FichaCompleta`), para tipar payloads en cliente y servidor.
//
// Reglas de diseño:
// 1. Los bloques condicionales (descalcificador, ósmosis) son opcionales
//    a nivel TypeScript: si el flag es false, los detalles no aplican.
//    Usamos `discriminatedUnion` para que el tipo sea estrecho según el flag.
// 2. Los campos numéricos vienen del formulario como `string` cuando salen
//    del input; aquí los aceptamos como `coerce.number()` para no obligar
//    al cliente a parsear antes de validar.
// 3. Lo opcional realmente opcional (puede no venir) → `.optional()`.
//    Lo opcional "por defecto null" → `.nullable()`.
import { z } from 'zod';
import { TIPO_RESIDENCIA, SI_NO, EQUIPOS_DESCALCIFICADOR, EQUIPOS_OSMOSIS, VASO_EXPANSION, LLAVE_PASO, } from './catalogos.js';
// ---- Sub-bloques ----------------------------------------------------------
export const datosClienteSchema = z.object({
    nombre: z.string().trim().min(2, 'Indica el nombre completo'),
    telefono: z.string().trim().regex(/^\+?[\d\s.-]{6,}$/, 'Teléfono inválido'),
    tipoResidencia: z.enum(TIPO_RESIDENCIA),
    planta: z.coerce.number().int().min(0).max(10),
    nPersonas: z.coerce.number().int().min(1).max(10),
    pe: z.coerce.number().nonnegative(),
    pd: z.coerce.number().nonnegative(),
    ga: z.coerce.number().nonnegative(),
    th: z.coerce.number().nonnegative(),
    tds: z.coerce.number().nonnegative(),
    ph: z.coerce.number().min(0).max(14),
    mascota: z.enum(SI_NO),
    pielAtopica: z.enum(SI_NO),
});
export const descalcificadorSiSchema = z.object({
    presente: z.literal(true),
    nRegistros: z.coerce.number().int().min(0).max(3),
    distanciaLuzM: z.coerce.number().nonnegative(),
    distanciaDesagueM: z.coerce.number().nonnegative(),
    pasarParedes: z.coerce.number().int().min(0).max(3),
    llavePasoPrefiltro: z.enum(LLAVE_PASO),
    tiempoInstalacion: z.string().trim().min(1),
    descripcionInstalacion: z.string().trim().max(2000),
    equipo: z.enum(EQUIPOS_DESCALCIFICADOR),
});
export const osmosisSiSchema = z.object({
    presente: z.literal(true),
    distanciaLuzM: z.coerce.number().nonnegative(),
    distanciaDesagueM: z.coerce.number().nonnegative(),
    tiempoInstalacion: z.string().trim().min(1),
    descripcionInstalacion: z.string().trim().max(2000),
    equipo: z.enum(EQUIPOS_OSMOSIS),
});
const ausenteSchema = z.object({ presente: z.literal(false) });
export const descalcificadorSchema = z.discriminatedUnion('presente', [
    descalcificadorSiSchema,
    ausenteSchema,
]);
export const osmosisSchema = z.discriminatedUnion('presente', [
    osmosisSiSchema,
    ausenteSchema,
]);
export const otrosSchema = z.object({
    reguladorPresion: z.enum(SI_NO),
    prefiltroSedimentos: z.enum(SI_NO),
    vasoExpansion: z.enum(VASO_EXPANSION),
});
// ---- Ficha completa -------------------------------------------------------
/**
 * Payload de la ficha tal como la envía la PWA del técnico. Sin metadatos
 * del servidor (id, created_at, archivos subidos a OneDrive — eso lo añade
 * el backend al persistir).
 */
export const fichaInputSchema = z.object({
    cliente: datosClienteSchema,
    descalcificador: descalcificadorSchema,
    osmosis: osmosisSchema,
    otros: otrosSchema,
    // Notas libres del técnico (opcional). Las fotos/vídeos NO van aquí:
    // se gestionan por separado en su cola de subida.
    notas: z.string().trim().max(5000).optional(),
}).refine(
// Validación cruzada: alguna de las dos secciones tiene que estar
// presente — no tiene sentido una ficha sin descalcificador ni ósmosis.
(f) => f.descalcificador.presente || f.osmosis.presente, { message: 'Marca al menos descalcificador u ósmosis.' });
/** Estado del ciclo de vida de una ficha en el servidor. */
export const ESTADO_FICHA = [
    'borrador', // creada por el técnico, todavía editable
    'recibida', // enviada por el técnico, oficina la ve
    'en_dimensionado', // alguien de oficina está trabajando en ella
    'presupuestada', // se envió presupuesto al cliente
    'aceptada',
    'rechazada',
    'archivada',
];
//# sourceMappingURL=ficha-schema.js.map